// ============================================================================
// SessionDetailSheet — the calendar's appointment DETAIL + INLINE EDIT panel.
// ----------------------------------------------------------------------------
// Tapping any session on the calendar (a month event chip, a week event, or a
// day-list row) opens this mobile-first sheet (bottom sheet on phones, centered
// dialog on ≥sm). It shows the full appointment — client (linked), date/time,
// duration, type, location, status and fee in the trainer's currency — then
// lets the trainer flip to EDIT and change date/time, duration, location, type,
// status and fee/override IN PLACE, saving via PATCH /api/sessions/{id} and
// deleting via the API, all WITHOUT leaving the calendar.
//
// Live-sync + optimism: useUpdateSession/useDeleteSession already update the
// session detail optimistically and invalidate the trainer-wide calendar feed
// on settle, so the grid reflects the change immediately; the WebSocket
// live-sync layer (sync/useLiveSync) fans the same change out to other devices.
// The trainer's HK$ / US$ / £ symbol is resolved through the shared currency
// util (lib/currency) so money reads the same here as everywhere else.
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  AttendanceControls,
  Avatar,
  Badge,
  Button,
  SelectField,
  TextField,
} from "../components";
import {
  IconCalendar,
  IconClock,
  IconMapPin,
} from "../components/icons";
import { useAccount } from "../hooks/account";
import { useClient } from "../hooks/clients";
import { useDeleteSession, useUpdateSession } from "../hooks/sessions";
import type { SessionInput } from "../api";
import {
  computeSessionFee,
  currencySymbol,
  formatMoney,
  isRateType,
  roundFee,
} from "../lib/currency";
import { cn } from "../lib/cn";
import { formatDateTimeLong } from "../lib/date";
import { Sheet } from "./Sheet";
import { SESSION_STYLE } from "./sessionStyle";
import type { SessionStatus, SessionType, SessionVM } from "./viewModel";

interface SessionDetailSheetProps {
  /** The session to show; null closes the sheet. */
  session: SessionVM | null;
  onClose: () => void;
}

const DURATION_PRESETS = [30, 45, 60, 90];
const TYPE_OPTIONS: SessionType[] = [
  "strength",
  "cardio",
  "mobility",
  "hiit",
  "mixed",
];

// --- pure datetime codecs (mirror sessions/lib so we never cross-import) -----

function p2(n: number): string {
  return String(n).padStart(2, "0");
}

/** "YYYY-MM-DDTHH:mm" (calendar VM startsAt) -> <datetime-local> value. */
function startsAtToLocal(startsAt: string): string {
  const m = startsAt.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (m) return `${m[1]}T${m[2]}`;
  const d = new Date(startsAt);
  if (Number.isNaN(d.getTime())) return "";
  return (
    `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}` +
    `T${p2(d.getHours())}:${p2(d.getMinutes())}`
  );
}

/**
 * <datetime-local> value (device-local wall-clock) -> the timezone-aware UTC
 * ISO the API stores. The picker yields a local instant; `new Date(localStr)`
 * interprets it in the device timezone and `toISOString()` converts to UTC, so a
 * HK trainer scheduling "10:00 PM" stores the correct UTC instant (and reads
 * back as 10:00 PM locally) instead of being mis-stamped as 10:00 PM UTC.
 */
function localToIso(v: string): string | null {
  const s = v.trim();
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function parseNum(v: string): number | null {
  const s = v.trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function parseInt0(v: string): number | null {
  const n = parseNum(v);
  if (n == null) return null;
  const i = Math.trunc(n);
  return i >= 0 ? i : null;
}

/**
 * Locale-aware long datetime for the view header, e.g. "Mon, 16 Jun 2026,
 * 2:30 PM". Centralized in lib/date so it follows the active i18n locale (month
 * names localize under 繁中) and the device-local timezone. `startsAt` is a
 * local wall-clock string; fall back to the raw value if unparseable.
 */
function fmtWhen(startsAt: string): string {
  const local = startsAtToLocal(startsAt);
  return formatDateTimeLong(local || startsAt) || startsAt;
}

export function SessionDetailSheet({
  session,
  onClose,
}: SessionDetailSheetProps) {
  const { t } = useTranslation("dashboard");
  const account = useAccount();
  const updateSession = useUpdateSession();
  const deleteSession = useDeleteSession();

  const [editing, setEditing] = useState(false);

  // --- edit form state ------------------------------------------------------
  const [status, setStatus] = useState<SessionStatus>("planned");
  const [when, setWhen] = useState("");
  const [duration, setDuration] = useState("");
  const [locationText, setLocationText] = useState("");
  // Keep the saved-place id when the trainer doesn't change the location text,
  // so an edit that only touches the time doesn't unpin a saved location.
  const [locationId, setLocationId] = useState<number | null>(null);
  const [type, setType] = useState<string>("");
  const [fee, setFee] = useState("");
  const [feeOverridden, setFeeOverridden] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>();

  // (Re)seed the form whenever a new session opens; always start in view mode.
  useEffect(() => {
    if (!session) return;
    setEditing(false);
    setSubmitError(undefined);
    setStatus(session.status);
    setWhen(startsAtToLocal(session.startsAt));
    setDuration(session.durationMin ? String(session.durationMin) : "");
    setLocationText(session.location ?? "");
    setLocationId(session.locationId ?? null);
    // Initialize the type <select> from the session's STORED type so opening
    // Edit preserves it (the bug: it used to fall to the first option,
    // "strength", silently corrupting the type on Save). Prefer the raw backend
    // string when it's one of the offered options; otherwise fall back to the
    // bucketed `type`, which is always a valid option — so the control always
    // shows the session's real type rather than defaulting to Strength.
    const stored = session.rawType ?? session.type;
    setType(
      (TYPE_OPTIONS as string[]).includes(stored) ? stored : session.type,
    );
    setFee(session.feeAmount != null ? String(session.feeAmount) : "");
    setFeeOverridden(session.feeAmount != null);
  }, [session]);

  // The trainer bills in one currency (account.default_currency); the session
  // may carry its own snapshot. Prefer the session's, fall back to account.
  const currency =
    (session?.currency || account.data?.default_currency || "").trim();
  const symbol = currencySymbol(currency);

  // Resolve the client's charge model so the fee can auto-fill (matches the
  // SessionForm behaviour: per_session = flat, per_hour = rate × duration/60).
  const clientQuery = useClient(session?.clientId);
  const rateClient = clientQuery.data ?? null;

  const durationMin = parseInt0(duration);
  const autoFee = useMemo(() => {
    if (!rateClient || !isRateType(rateClient.rate_type)) return null;
    const f = computeSessionFee(
      rateClient.rate_type,
      rateClient.rate_amount,
      null,
      durationMin,
    );
    return f == null ? null : roundFee(f);
  }, [rateClient, durationMin]);

  // While not overridden, track the auto-computed fee (so changing duration on
  // an hourly client updates the field live, before any round-trip).
  useEffect(() => {
    if (!editing || feeOverridden) return;
    setFee(autoFee != null ? String(autoFee) : "");
  }, [autoFee, feeOverridden, editing]);

  const hasRate = rateClient != null && isRateType(rateClient.rate_type);
  const rateHint = hasRate
    ? rateClient!.rate_type === "per_hour"
      ? t("detail.feeAutoHourly", {
          rate: formatMoney(rateClient!.rate_amount, currency, { symbol }),
        })
      : t("detail.feeAutoSession", {
          rate: formatMoney(rateClient!.rate_amount, currency, { symbol }),
        })
    : undefined;

  const busy = updateSession.isPending || deleteSession.isPending;

  const onSave = () => {
    if (!session) return;
    setSubmitError(undefined);
    const iso = localToIso(when);
    const trimmedLoc = locationText.trim();
    const body: SessionInput = {
      status,
      duration_min: parseInt0(duration),
      // Keep the pinned saved-place only when the text is unchanged from it;
      // any free-text edit detaches the location_id.
      location_id: trimmedLoc === (session.location ?? "") ? locationId : null,
      location_text: trimmedLoc || null,
      type: type.trim() || null,
      fee_amount: parseNum(fee),
      currency: currency || null,
    };
    // The backend reads scheduled_at for planned and performed_at for completed.
    if (status === "completed") {
      body.performed_at = iso;
      body.scheduled_at = null;
    } else {
      body.scheduled_at = iso;
      body.performed_at = null;
    }
    updateSession.mutate(
      { sessionId: session.id, body },
      {
        onSuccess: () => {
          // Optimistic + invalidation already refresh the calendar; close.
          onClose();
        },
        onError: () => setSubmitError(t("detail.saveError")),
      },
    );
  };

  const onDelete = () => {
    if (!session) return;
    if (!window.confirm(t("detail.deleteConfirm"))) return;
    deleteSession.mutate(session.id, { onSuccess: () => onClose() });
  };

  const st = session ? SESSION_STYLE[session.type] : null;
  const typeLabel = (tp: SessionType) => t(`sessionType.${tp}`);

  return (
    <Sheet
      open={session != null}
      onClose={onClose}
      size="lg"
      title={
        session ? (
          <span className="inline-flex items-center gap-2.5">
            <span
              className={cn("h-6 w-1.5 shrink-0 rounded-pill", st?.grad)}
              aria-hidden
            />
            {editing ? t("detail.editTitle") : session.clientName}
          </span>
        ) : (
          ""
        )
      }
      subtitle={
        session && !editing ? fmtWhen(session.startsAt) : undefined
      }
      footer={
        session ? (
          editing ? (
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => setEditing(false)}
                disabled={busy}
              >
                {t("detail.cancel")}
              </Button>
              <div className="flex-1" />
              <Button onClick={onSave} disabled={busy}>
                {busy ? t("detail.saving") : t("detail.save")}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={onDelete}
                disabled={busy}
                className="!text-danger"
              >
                {t("detail.delete")}
              </Button>
              <div className="flex-1" />
              <Button onClick={() => setEditing(true)}>
                {t("detail.edit")}
              </Button>
            </div>
          )
        ) : undefined
      }
    >
      {!session ? null : editing ? (
        // ---------------------------------------------------------------- EDIT
        <div className="space-y-5 py-1">
          {/* Status */}
          <div>
            <p className="mb-1.5 font-display text-sm font-bold text-body">
              {t("detail.status")}
            </p>
            <div className="grid grid-cols-2 gap-1 rounded-pill bg-surface-sunken p-1">
              <StatusBtn
                active={status === "planned"}
                onClick={() => setStatus("planned")}
                icon={<IconCalendar width={16} height={16} />}
              >
                {t("detail.statusPlanned")}
              </StatusBtn>
              <StatusBtn
                active={status === "completed"}
                onClick={() => setStatus("completed")}
                icon={<IconClock width={16} height={16} />}
              >
                {t("detail.statusCompleted")}
              </StatusBtn>
            </div>
          </div>

          {/* Date & time */}
          <div>
            <label
              htmlFor="vf-cal-when"
              className="mb-1.5 block font-display text-sm font-bold text-body"
            >
              {t("detail.when")}
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-body-faint">
                <IconCalendar width={18} height={18} />
              </span>
              <input
                id="vf-cal-when"
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="h-12 w-full rounded-md bg-surface pl-10 pr-4 text-base text-body ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
              />
            </div>
          </div>

          {/* Duration */}
          <div>
            <p className="mb-1.5 font-display text-sm font-bold text-body">
              {t("detail.duration")}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {DURATION_PRESETS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDuration(String(d))}
                  aria-pressed={duration.trim() === String(d)}
                  className={cn(
                    "rounded-pill px-4 py-2 text-sm font-bold transition-all duration-fast",
                    duration.trim() === String(d)
                      ? "bg-grad-coral text-white shadow-volt"
                      : "bg-surface-sunken text-body-muted ring-1 ring-inset ring-line hover:bg-line",
                  )}
                >
                  {d}
                </button>
              ))}
              <div className="w-28">
                <TextField
                  label={t("detail.duration")}
                  hideLabel
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  suffix={t("detail.minutes")}
                />
              </div>
            </div>
          </div>

          {/* Type + Location */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SelectField
              label={t("detail.type")}
              value={type}
              onChange={(e) => setType(e.target.value)}
              options={TYPE_OPTIONS.map((tp) => ({
                label: typeLabel(tp),
                value: tp,
              }))}
            />
            <TextField
              label={t("detail.location")}
              placeholder={t("detail.locationPlaceholder")}
              value={locationText}
              onChange={(e) => {
                setLocationText(e.target.value);
                // Free-text edit unpins any saved-place id (handled in onSave).
              }}
              iconLeft={<IconMapPin width={18} height={18} />}
            />
          </div>

          {/* Fee + override */}
          <div>
            <TextField
              label={t("detail.fee")}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={fee}
              onChange={(e) => {
                setFee(e.target.value);
                setFeeOverridden(true);
              }}
              hint={!feeOverridden ? rateHint : undefined}
              suffix={symbol || undefined}
            />
            {feeOverridden && autoFee != null && (
              <button
                type="button"
                onClick={() => setFeeOverridden(false)}
                className="mt-1.5 text-sm font-bold text-coral transition-colors hover:text-coral-600 focus-visible:outline-none focus-visible:underline"
              >
                {t("detail.feeResetAuto", {
                  amount: formatMoney(autoFee, currency, { symbol }),
                })}
              </button>
            )}
          </div>

          {submitError && (
            <p className="text-sm font-medium text-danger" role="alert">
              {submitError}
            </p>
          )}
        </div>
      ) : (
        // ---------------------------------------------------------------- VIEW
        <div className="space-y-4 py-1">
          {/* Client + status */}
          <div className="flex items-center gap-3">
            <Avatar name={session.clientName} size="md" />
            <div className="min-w-0 flex-1">
              <Link
                to={`/app/clients/${session.clientId}`}
                className="inline-flex items-center gap-1 font-display text-base font-bold text-body transition-colors hover:text-coral focus-visible:outline-none focus-visible:underline"
              >
                {session.clientName}
                <span aria-hidden className="text-coral">
                  ›
                </span>
              </Link>
              <p className="text-sm text-body-soft">
                {t("detail.viewClientLink")}
              </p>
            </div>
            <Badge
              tone={session.status === "completed" ? "success" : "neutral"}
              dot
              className="shrink-0"
            >
              {session.status === "completed"
                ? t("detail.statusCompleted")
                : t("detail.statusPlanned")}
            </Badge>
          </div>

          {/* Detail rows */}
          <dl className="divide-y divide-line overflow-hidden rounded-md ring-1 ring-line">
            <DetailRow
              icon={<IconCalendar width={17} height={17} />}
              label={t("detail.when")}
              value={fmtWhen(session.startsAt)}
            />
            <DetailRow
              icon={<IconClock width={17} height={17} />}
              label={t("detail.duration")}
              value={
                session.durationMin
                  ? t("detail.minutesValue", { count: session.durationMin })
                  : "—"
              }
            />
            <DetailRow
              icon={
                <span
                  className={cn("h-3 w-3 rounded-full", st?.dot)}
                  aria-hidden
                />
              }
              label={t("detail.type")}
              value={session ? typeLabel(session.type) : "—"}
            />
            <DetailRow
              icon={<IconMapPin width={17} height={17} />}
              label={t("detail.location")}
              value={session.location || "—"}
            />
            <DetailRow
              icon={<FeeGlyph />}
              label={t("detail.fee")}
              value={
                session.feeAmount != null
                  ? formatMoney(session.feeAmount, currency, { symbol })
                  : "—"
              }
              accessory={
                session.feeAmount != null ? (
                  <Badge
                    tone={session.paid ? "success" : "warning"}
                    className="shrink-0"
                  >
                    {session.paid ? t("detail.paid") : t("detail.notPaid")}
                  </Badge>
                ) : undefined
              }
            />
          </dl>

          {/* Attendance / check-in: mark arrived (optional time), no-show, and
              show the check-in QR for the client to scan in person. Attending
              flips the session to completed (the calendar refetches on settle). */}
          <AttendanceControls
            sessionId={session.id}
            attendance={session.attendance}
            checkedInAt={session.checkedInAt}
            clientName={session.clientName}
          />

          {session.notes && (
            <div className="rounded-md bg-surface-soft p-3 ring-1 ring-line">
              <p className="mb-1 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
                {t("detail.notes")}
              </p>
              <p className="whitespace-pre-line text-sm text-body">
                {session.notes}
              </p>
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
}

function DetailRow({
  icon,
  label,
  value,
  accessory,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  accessory?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 bg-surface px-4 py-3">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-surface-sunken text-body-soft">
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <dt className="text-xs font-bold uppercase tracking-kicker text-body-faint">
          {label}
        </dt>
        <dd className="truncate font-display text-sm font-bold text-body">
          {value}
        </dd>
      </div>
      {accessory}
    </div>
  );
}

function StatusBtn({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-pill px-3 py-2.5 font-display text-sm font-bold transition-all duration-fast",
        active ? "bg-surface text-body shadow-xs" : "text-body-soft hover:text-body",
      )}
    >
      {icon}
      <span className="truncate">{children}</span>
    </button>
  );
}

/** A tiny coin glyph for the fee row (no shared money icon in the set). */
function FeeGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-[17px] w-[17px]" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 8v8M9.5 10h3.25a1.75 1.75 0 0 1 0 3.5H9.5h4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
